import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Mail, Ticket, Crown, CheckCircle2 } from 'lucide-react';
import { ClaimSpotBox } from './ClaimSpotBox';

const API = process.env.REACT_APP_BACKEND_URL;
const statusTone = { confirmed: 'bg-emerald-50 text-emerald-700', locked: 'bg-amber-50 text-amber-700', forming: 'bg-blue-50 text-blue-700', cancelled: 'bg-gray-100 text-gray-500' };

// "My Hackathons" — team history + pending invites for the logged-in student
export const MyHackathonsSection = () => {
  const token = localStorage.getItem('codearena_token');
  const [history, setHistory] = useState([]);
  const [invites, setInvites] = useState([]);
  const [claimable, setClaimable] = useState(0);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [h, i] = await Promise.all([
        axios.get(`${API}/api/event-teams/me/history`, { headers }),
        axios.get(`${API}/api/event-teams/me/invites`, { headers }),
      ]);
      setHistory(h.data.history || []);
      setClaimable(h.data.claimable || 0);
      setInvites(i.data.invites || []);
    } catch { /* not logged in or expired */ }
  };
  useEffect(() => { if (token) load(); }, []);

  const respond = async (kind, id) => {
    try {
      const r = await axios.post(`${API}/api/event-teams/invites/${kind}`, { invite_id: id }, { headers });
      toast.success(r.data.message); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!token || (history.length === 0 && invites.length === 0 && !claimable)) return null;
  return (
    <div className="mb-8 space-y-4" data-testid="my-hackathons-section">
      {claimable > 0 && <ClaimSpotBox onLinked={load} text={<>{claimable} hackathon team spot{claimable > 1 ? 's are' : ' is'} reserved for your email. Verify your email to see your team, payment and ticket here.</>} />}
      {invites.map((inv) => (
        <div key={inv.id} className="flex items-center justify-between gap-3 bg-white border border-[#0a66c2]/30 rounded-xl p-4" data-testid={`dash-invite-${inv.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            <Mail size={18} className="text-[#0a66c2] flex-shrink-0" />
            <p className="text-sm text-[#191919] truncate"><b>{inv.invited_by_name || 'A teammate'}</b> invited you to team <b>{inv.team_name}</b> for {inv.event_title}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => respond('accept', inv.id)} className="px-3 py-1.5 bg-[#0a66c2] text-white rounded-lg text-xs font-semibold" data-testid={`dash-accept-${inv.id}`}>Accept</button>
            <button onClick={() => respond('decline', inv.id)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-[#666]">Decline</button>
          </div>
        </div>
      ))}
      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
            <Trophy size={16} className="text-[#F59E0B]" />
            <h2 className="font-semibold text-[#191919] text-sm">My Hackathons & Teams</h2>
            <span className="text-xs text-[#666]">({history.length})</span>
          </div>
          <div className="divide-y divide-gray-100">
            {history.map((h) => (
              <div key={h.team_id + (h.joined_at || '')} className="px-5 py-3.5 flex items-center justify-between gap-4 flex-wrap" data-testid={`history-row-${h.team_id}`}>
                <div className="min-w-0">
                  <Link to={`/events/${h.event.id}`} className="font-medium text-[#191919] hover:text-[#0a66c2] text-sm">{h.event.title || 'Event'}</Link>
                  <p className="text-xs text-[#666] mt-0.5 flex items-center gap-1.5">
                    {h.role === 'leader' && <Crown size={12} className="text-[#F59E0B]" />} Team {h.team_name} · {h.member_count} members
                    {h.event.event_date && ` · ${new Date(h.event.event_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {h.membership_status !== 'active'
                    ? <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500 capitalize">{h.membership_status}</span>
                    : <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${statusTone[h.team_status] || ''}`}>{h.team_status}</span>}
                  {h.payment_status && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-50 text-[#666] capitalize" data-testid={`history-payment-${h.team_id}`}>{h.payment_status.replace('_', ' ')}</span>}
                  {h.checked_in && <span className="flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 size={12} /> Attended</span>}
                  {h.ticket_status === 'active' && h.registration_id && (
                    <Link to={`/events/${h.event.id}/ticket/${h.registration_id}`} className="flex items-center gap-1 text-xs font-medium text-[#0a66c2]" data-testid={`history-ticket-${h.team_id}`}><Ticket size={13} /> Ticket</Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
